import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, PenLineIcon, UserPlusIcon } from 'lucide-react';
import { ListPageLayout } from '../components/lists/ListPageLayout';
import { LogCommunicationModal } from '../components/communication/LogCommunicationModal';
import { AddLeadDrawer } from '../components/leads/AddLeadDrawer';
import { useToast } from '../contexts/ToastContext';
import { leads } from '../data/leads';
import type { CommunicationSubject, Lead } from '../types/portal';
const filters = [{
  id: 'all',
  label: 'All'
}, {
  id: 'new',
  label: 'New'
}, {
  id: 'working',
  label: 'Working'
}, {
  id: 'nurture',
  label: 'Nurture'
}, {
  id: 'uncontacted',
  label: 'Never contacted'
}];
const statusBadge: Record<Lead['status'], string> = {
  new: 'bg-brand-50 text-brand-700 ring-brand-200',
  working: 'bg-teal-50 text-teal-700 ring-teal-200',
  nurture: 'bg-navy-50 text-navy-600 ring-navy-200'
};
export function MyLeadsPage() {
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [subject, setSubject] = useState<CommunicationSubject | null>(null);
  const [leadList, setLeadList] = useState<Lead[]>(leads);
  const [addOpen, setAddOpen] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setLoading(false), 600);
    return () => window.clearTimeout(timer);
  }, []);
  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leadList.filter(lead => {
      const matchesFilter = filter === 'all' ? true : filter === 'uncontacted' ? lead.lastContactLabel === null : lead.status === filter;
      const matchesSearch = term.length === 0 || lead.name.toLowerCase().includes(term) || lead.id.toLowerCase().includes(term) || lead.source.toLowerCase().includes(term);
      return matchesFilter && matchesSearch;
    });
  }, [filter, leadList, search]);
  const filterChips = filters.map(item => ({
    ...item,
    count: item.id === 'all' ? leadList.length : item.id === 'uncontacted' ? leadList.filter(lead => lead.lastContactLabel === null).length : leadList.filter(lead => lead.status === item.id).length
  }));
  return <>
      <ListPageLayout title="My Leads" subtitle="Consumer leads assigned to you. Log every touch manually — outbound calls from your own phone aren’t tracked automatically." actions={<button type="button" onClick={() => setAddOpen(true)} className="inline-flex h-11 items-center gap-2 rounded-lg bg-navy-800 px-4 text-sm font-semibold text-white hover:bg-navy-900" data-id="element-695" data-mp="20ci9o82a28h9">
            <UserPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-696" data-mp="owbhvvoapmwx" />
            Add lead
          </button>} searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search leads by name, ID, or source" filters={filterChips} activeFilter={filter} onFilterChange={setFilter} loading={loading} isEmpty={visible.length === 0} emptyTitle="No leads match this view" emptyMessage="Try a different filter or clear your search. New leads appear here the moment they’re assigned to you." resultLabel={`Showing ${visible.length} of ${leadList.length} leads`} data-id="element-694" data-mp="29ehr31h7fekq">
        <ul className="divide-y divide-navy-100" data-id="element-697" data-mp="3jb69em8hm0sg">
          {visible.map(lead => <li key={lead.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-698" data-mp="3ob2ekosx7y9l">
              <div className="min-w-0" data-id="element-699" data-mp="3sal11p0e4hej">
                <div className="flex flex-wrap items-center gap-2" data-id="element-700" data-mp="1ty2xssct0y0q">
                  <Link to={`/leads/${lead.id}`} className="text-sm font-semibold text-navy-900 hover:text-brand-700" data-id="element-701" data-mp="1xywlplq8yctm">
                    {lead.name}
                  </Link>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ${statusBadge[lead.status]}`} data-id="element-702" data-mp="ym33kshpdpix">
                    {lead.status}
                  </span>
                  {lead.lastContactLabel === null ? <span className="inline-flex items-center gap-1 rounded-full bg-danger-50 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-danger-700 ring-1 ring-danger-200" data-id="element-703" data-mp="jyi190snjuc2">
                      <ClockIcon className="h-3 w-3" aria-hidden="true" data-id="element-704" data-mp="tpcnw69t8vy1" />
                      Never contacted
                    </span> : null}
                </div>
                <p className="mt-1 text-sm text-navy-700" data-id="element-705" data-mp="16fq24ys5697">
                  {lead.purpose} · {lead.amount} · {lead.program} ·{' '}
                  {lead.market}
                </p>
                <p className="mt-1 text-xs text-navy-500" data-id="element-706" data-mp="27uy8hhbzphck">
                  {lead.id} · {lead.source} · {lead.assignedLabel}
                  {lead.lastContactLabel ? ` · ${lead.lastContactLabel}` : ''}
                </p>
              </div>

              <div className="flex shrink-0 gap-2" data-id="element-707" data-mp="1yt899pbv9e48">
                <button type="button" onClick={() => setSubject({
              id: lead.id,
              name: lead.name,
              context: `${lead.purpose} · ${lead.amount} · ${lead.program}`,
              phone: lead.phone,
              email: lead.email,
              goalCredit: 'your consumer conversations goal'
            })} className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-700 sm:flex-none" data-id="element-708" data-mp="22xy3l9qvmkn1">
                  <PenLineIcon className="h-4 w-4" aria-hidden="true" data-id="element-709" data-mp="3cvfodja35a73" />
                  Log communication
                </button>
                <Link to={`/leads/${lead.id}`} className="inline-flex h-11 min-w-[5.5rem] items-center justify-center rounded-lg border border-navy-200 px-3 text-sm font-semibold text-navy-700 hover:bg-navy-50" data-id="element-710" data-mp="37x4ex9npd1pe">
                  View
                </Link>
              </div>
            </li>)}
        </ul>
      </ListPageLayout>

      <LogCommunicationModal subject={subject} onClose={() => setSubject(null)} onLogged={showToast} data-id="element-711" data-mp="2p4a7d0sesbv7" />
      <AddLeadDrawer open={addOpen} existingLeads={leadList} onClose={() => setAddOpen(false)} onCreate={lead => {
      // Keep the shared record store in sync so the lead detail page resolves it.
      leads.unshift(lead);
      setLeadList([...leads]);
      setFilter('all');
      setSearch('');
      showToast(`${lead.name} added to your leads — log first contact next.`);
    }} data-id="element-712" data-mp="2k8beazw4y5og" />
    </>;
}